import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck, Trash2, MapPin, Wifi, WifiOff, Navigation, Square, Gauge, AlertTriangle, LogIn, LogOut } from 'lucide-react';
import { notificationsActions } from '@/store';

const EVENT_ICONS = {
  deviceOnline: { icon: Wifi, color: '#10b981' },
  deviceOffline: { icon: WifiOff, color: '#94a3b8' },
  deviceMoving: { icon: Navigation, color: '#3b82f6' },
  deviceStopped: { icon: Square, color: '#ef4444' },
  deviceOverspeed: { icon: Gauge, color: '#f59e0b' },
  geofenceEnter: { icon: LogIn, color: '#8b5cf6' },
  geofenceEntry: { icon: LogIn, color: '#8b5cf6' }, // rule fires carry the condition type (TASK-338)
  geofenceExit: { icon: LogOut, color: '#f97316' },
  alarm: { icon: AlertTriangle, color: '#ef4444' },
  ignitionOn: { icon: Wifi, color: '#10b981' },
  ignitionOff: { icon: WifiOff, color: '#94a3b8' },
};

const DEFAULT_ICON = { icon: MapPin, color: '#3b82f6' };

function timeAgo(timestamp, t) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return t('notifications.justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export default function NotificationBell({ variant = 'solid' }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useSelector((state) => state.notifications.items);
  const unreadCount = useSelector((state) => state.notifications.unreadCount);
  const devices = useSelector((state) => state.devices.items);
  const positions = useSelector((state) => state.session.positions);
  const staffByUserId = useSelector((state) => state.staff.byUserId);

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Resolve the lat/lng a notification refers to. Priority:
  //   1. notif.position (rule fires carry the fix that triggered them)
  //   2. devices[deviceId] / positions[deviceId] from Redux
  //   3. staffByUserId[userId].lastFix (when the rule fired on a staff phone)
  const resolveCoords = (notif) => {
    const p = notif.position;
    if (p?.latitude != null && p?.longitude != null) {
      return { lat: p.latitude, lng: p.longitude };
    }
    if (notif.deviceId != null) {
      const pos = positions[notif.deviceId];
      if (pos?.latitude != null && pos?.longitude != null) {
        return { lat: pos.latitude, lng: pos.longitude };
      }
    }
    const userId = p?.userId;
    if (userId) {
      const fix = staffByUserId[userId]?.lastFix;
      if (fix?.latitude != null && fix?.longitude != null) {
        return { lat: fix.latitude, lng: fix.longitude };
      }
    }
    return null;
  };

  const handleItemClick = (notif) => {
    dispatch(notificationsActions.markRead(notif._id));
    setOpen(false);
    const coords = resolveCoords(notif);
    if (coords) {
      // sessionStorage handoff covers the "user wasn't on /map yet" case —
      // LiveMapPage consumes it on mount. The window event covers the case
      // where they were already on /map (no remount happens).
      try {
        sessionStorage.setItem('trackizy_livemap_fly_to', JSON.stringify(coords));
      } catch { /* quota / private-mode — non-fatal */ }
      window.dispatchEvent(new CustomEvent('livemap-fly-to', { detail: coords }));
    }
    navigate('/map');
  };

  const isGlass = variant === 'glass';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`relative p-2 rounded-xl transition-all ${
          isGlass ? 'hover:bg-white/40' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
        } ${open ? (isGlass ? 'bg-white/40' : 'bg-slate-100 dark:bg-slate-800') : ''}`}
      >
        <Bell size={16} className="text-slate-500" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold ring-2 ring-white dark:ring-slate-900 shadow-sm px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          // Non-glass: solid card, same as AppDownloadMenu. (It used to set an
          // invalid inline `background` string, so the panel was see-through.)
          className={`absolute end-0 top-full mt-1.5 w-80 rounded-xl overflow-hidden shadow-xl z-50 ${
            isGlass ? '' : 'bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800'
          }`}
          style={isGlass
            ? { background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.5)' }
            : undefined
          }
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300">{t('notifications.title')}</h4>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={() => dispatch(notificationsActions.markAllRead())}
                  className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-500 transition-all"
                  title={t('notifications.markAllRead')}
                >
                  <CheckCheck size={14} />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={() => dispatch(notificationsActions.clear())}
                  className="p-1 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all"
                  title={t('notifications.clearAll')}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-400">
                <Bell size={20} className="mx-auto mb-2 opacity-30" />
                {t('notifications.empty')}
              </div>
            ) : (
              notifications.map((notif) => {
                const evtConfig = EVENT_ICONS[notif.type] || DEFAULT_ICON;
                const Icon = evtConfig.icon;
                return (
                  <button
                    key={notif._id}
                    onClick={() => handleItemClick(notif)}
                    className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-start transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 ${
                      !notif._read ? 'bg-blue-50/40 dark:bg-blue-500/10' : ''
                    }`}
                    style={{ borderBottom: '1px solid #f1f5f9' }}
                  >
                    {!notif._read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                    )}
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: evtConfig.color + '15' }}
                    >
                      <Icon size={13} style={{ color: evtConfig.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                        {t(`alerts.events.${notif.type}`, notif.type)}
                      </p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {notif.deviceName
                          || devices[notif.deviceId]?.name
                          || staffByUserId[notif.position?.userId]?.name
                          || notif.snapshot?.poiName
                          || '—'}
                        {notif.locationLabel ? ` · ${notif.locationLabel}` : ''}
                      </p>
                    </div>
                    <span className="text-[10px] text-slate-400 flex-shrink-0 mt-0.5">
                      {timeAgo(notif._timestamp, t)}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-800 p-2">
              <button
                onClick={() => { setOpen(false); navigate('/alerts'); }}
                className="w-full text-center text-[11px] text-blue-500 font-medium py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
              >
                {t('notifications.viewAll')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
